import { Badge } from '@/ui-components/atoms/Badge/Badge';
import { Button, type ButtonProps } from '@/ui-components/atoms/Button/Button';
import styles from './header.module.scss';
import { Text } from '@/ui-components/atoms/Text/Text';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { HeaderProps } from './Header';
import { ColBreakpoints } from '@/ui-components/atoms/Layout/Grid';
import cn from 'classnames';
import { AnimatedHeading } from './AnimatedHeading';
import { CTAGroup } from '@/ui-components/molecules/CTAGroup/CTAGroup';

export const HeaderCenterOrLeft = ({
    badge,
    heading,
    headingTag,
    subhead,
    channelNumber,
    caption,
    links,
    ctaGroupAlignment = 'left',
    theme = 'center',
    inheritTextColor = false,
    subHeadAbove = false,
    className,
    headingAriaLabel,
    type,
}: HeaderProps): React.JSX.Element => {
    const setThemeCols = (cols: ColBreakpoints): ColBreakpoints =>
        theme === 'full-width-center' ? { sm: 12 } : theme === 'left' ? {} : cols;

    const inPromoBannerCard = type === 'promoBannerCard';
    const containerClasses = cn(styles[theme], styles.container, className, {
        [styles.headingElementSpacing]: inPromoBannerCard,
    });

    const badgeStyles = cn(styles.badgeContainer, {
        [styles.inCardStyles]: inPromoBannerCard,
    });

    const headingStyles = cn(headingTag || 'h2', styles.heading, {
        [styles.headingInPromoBannerCard]: inPromoBannerCard,
        [styles.headingInHeroHeader]: type === 'heroHeader' && theme === 'left',
    });

    const subheadStyles = cn(inheritTextColor ? styles.subhead : styles.subheadDefault, {
        [styles.subheadInPromoBannerCard]: inPromoBannerCard,
        [styles.subheadInHeroHeader]: type === 'heroHeader',
        [styles.subheadInHeroHeaderLeft]: type === 'heroHeader' && theme === 'left',
        [styles.subheadInHeroHeaderAlwaysLeft]: type === 'heroHeader' && theme === 'always-left',
    });

    const buttonContainerStyles = cn(styles.buttonContainer, {
        [styles.leftAlignedButton]: theme === 'left',
    });

    const linkNodes: React.ReactElement<ButtonProps>[] = [];

    if (links && links.length > 0) {
        const mainLinks = links.filter((link) => (link as ButtonProps).variant !== 'tertiary');
        const hasTwoMain = mainLinks.length === 2;
        const isPromoBanner = type === 'promoBanner';

        for (let i = 0; i < 3; i += 1) {
            const link = links[i] as ButtonProps | undefined;
            if (!link) continue;

            const variant = link.variant;
            const fitting: ButtonProps['fitting'] = isPromoBanner
                ? 'fill-container'
                : variant === 'tertiary'
                  ? 'hug-contents'
                  : hasTwoMain
                    ? 'fill-container'
                    : 'hug-contents';

            linkNodes.push(<Button key={i} {...link} fitting={fitting} className={styles.button} />);
        }
    }

    return (
        <Grid col={{ sm: 12 }} className={containerClasses} container noPadding>
            <Grid col={{ sm: 12 }} className={styles.headingContainer + ' heading'} container>
                {subHeadAbove ? (
                    <>
                        {subhead && (
                            <Grid col={setThemeCols({ sm: 12, md: 6, lg: 6 })} start={setThemeCols({ lg: 4, md: 4 })}>
                                <Text
                                    tag="div"
                                    variant="text-xlarge"
                                    className={`${inheritTextColor ? styles.subhead : styles.subheadDefault} ${styles.subHeadAbove} ${type === 'heroHeader' ? styles.subheadInHeroHeader : ''}`}
                                >
                                    {channelNumber ? (
                                        <>
                                            CH {channelNumber}
                                            <br />
                                            {subhead}
                                        </>
                                    ) : (
                                        subhead
                                    )}
                                </Text>
                            </Grid>
                        )}
                        {heading && (
                            <Grid col={setThemeCols({ sm: 12, md: 8, lg: 8 })} start={setThemeCols({ md: 3, lg: 3 })}>
                                {badge && (
                                    <div className={badgeStyles}>
                                        <Badge {...badge} />
                                    </div>
                                )}
                                <AnimatedHeading
                                    ariaLabel={headingAriaLabel}
                                    tag={headingTag || 'h2'}
                                    className={headingStyles}
                                >
                                    {heading}
                                </AnimatedHeading>
                            </Grid>
                        )}
                    </>
                ) : (
                    <>
                        {heading && (
                            <Grid col={setThemeCols({ sm: 12, md: 8, lg: 8 })} start={setThemeCols({ md: 3, lg: 3 })}>
                                {badge && (
                                    <div className={badgeStyles}>
                                        <Badge {...badge} />
                                    </div>
                                )}
                                <AnimatedHeading
                                    ariaLabel={headingAriaLabel}
                                    tag={headingTag || 'h2'}
                                    className={headingStyles}
                                >
                                    {heading}
                                </AnimatedHeading>
                            </Grid>
                        )}

                        {subhead && (
                            <Grid col={setThemeCols({ sm: 12, md: 6, lg: 6 })} start={setThemeCols({ lg: 4, md: 4 })}>
                                <Text tag="div" variant="text-xlarge" className={subheadStyles}>
                                    {channelNumber ? (
                                        <>
                                            CH {channelNumber}
                                            <br />
                                            {subhead}
                                        </>
                                    ) : (
                                        subhead
                                    )}
                                </Text>
                            </Grid>
                        )}
                    </>
                )}
            </Grid>

            {!!links?.length && (
                <Grid className={buttonContainerStyles}>
                    <CTAGroup
                        links={linkNodes}
                        caption={caption}
                        desktopAlign={ctaGroupAlignment}
                        mobileAlign="center"
                    />
                </Grid>
            )}
        </Grid>
    );
};
